import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import api from "../../services/api";
import { getAgendaWithOfflineCache } from "../../services/agendaOfflineService";
import { openOCI } from "../../utils/openOCI";
import { getWorkorderDocuments } from "../../services/documentService";
import {
  createMonteurScopeKey,
  getCachedReferenceData,
  getCachedWorkorderCollection,
  getCachedWorkorderDetail,
  getCachedWorkorderFinancial,
  setCachedReferenceData,
  setCachedWorkorderCollection,
  setCachedWorkorderDetail,
  setCachedWorkorderFinancial,
} from "../../services/monteurOfflineService";
import { useUser } from "../../context/UserContext";
import WerkbonHeader from "../../components/monteur/werkbon-view/WerkbonHeader";
import WerkbonSidebar from "../../components/monteur/werkbon-view/WerkbonSidebar";
import WerkbonTabs from "../../components/monteur/werkbon-view/WerkbonTabs";
import ExtraTimeRequestModal from "../../components/time-requests/ExtraTimeRequestModal";
import {
  agendaItemOverlapsDay,
  compressImageFile,
  createLocalId,
  editorDestToFile,
  formatDatum,
  formatPlannedWindow,
  getWerkbonTitle,
  getPreferredOutputMimeType,
  isTodayDate,
  matchesStatusFilter,
  normalizeWerkbonPhotos,
  resolveTodayDailyLog,
  revokePreviewUrls,
  toISODate,
} from "./workbonViewUtils";
import {
  openDefaultEditor,
  setPlugins,
  plugin_crop,
  plugin_annotate,
  plugin_filter,
  plugin_resize,
} from "@pqina/pintura";
import locale_nl_NL from "@pqina/pintura/locale/nl_NL";
import "@pqina/pintura/pintura.css";

const TABS = [
  { key: "agenda", label: "Vandaag" },
  { key: "taken", label: "Acties" },
  { key: "veiligheid", label: "Melding" },
  { key: "werkbon", label: "Uitvoering" },
];

const ACTION_TYPE_OPTIONS = [
  { value: "voorbereiding", label: "Voorbereiding" },
  { value: "uitvoering", label: "Uitvoering" },
  { value: "blokkade", label: "Blokkade: ontbrekende info" },
  { value: "nazorg", label: "Nazorg" },
];

const PHOTO_CATEGORY_OPTIONS = [
  { value: "voor", label: "Voor" },
  { value: "na", label: "Na" },
  { value: "bewijs", label: "Bewijs" },
  { value: "overig", label: "Overig" },
];

const STOP_REASON_OPTIONS = [
  { code: "toegang", text: "Geen toegang" },
  { code: "materiaal", text: "Materiaal ontbreekt" },
  { code: "scope", text: "Fout werkbon" },
  { code: "veiligheid", text: "KIB kaart ontbreekt" },
  { code: "derde", text: "Installatie niet voorbereid" },
];

const DAILY_LOG_TIMEZONE = "Europe/Amsterdam";
const PHOTO_UPLOAD_TIMEOUT_MS = 45000;

setPlugins(plugin_crop, plugin_annotate, plugin_filter, plugin_resize);

const EMPTY_HOUR = {
  hourTypeId: "",
  description: "",
  hours: "",
};

const EMPTY_MATERIAL = {
  description: "",
  qty: "",
  unit: "st",
  supplier: "",
  supplierArticleNumber: "",
  unitSell: "",
  vatRate: "21",
};

const EMPTY_ADJUSTMENT = {
  category: "overig",
  description: "",
  hours: "",
  reason: "",
};

const ADJUSTMENT_CATEGORIES = [
  { value: "avond", label: "Avond" },
  { value: "nacht", label: "Nacht" },
  { value: "overuur", label: "Overuur" },
  { value: "weekend", label: "Weekend" },
  { value: "feestdag", label: "Feestdag" },
  { value: "reintegratie", label: "Re-integratie" },
  { value: "overig", label: "Overig" },
];
const DONE_RANGE_OPTIONS = [
  { key: "today", label: "Vandaag" },
  { key: "week", label: "Deze week" },
  { key: "all", label: "Alles" },
];
const SIGNATURE_DATA_URL_MAX_LENGTH = 750000;
const EMPTY_CUSTOMER_APPROVAL = Object.freeze({
  signedByName: "",
  signedByRole: "",
  approved: false,
  signatureDataUrl: "",
});

function isAssignedToCurrentUser(werkbon, user) {
  const userId = String(user?._id || user?.id || user?.userId || user?.sub || "");
  if (!userId) return false;

  const assignedToRaw = werkbon?.assignedTo?._id || werkbon?.assignedTo || "";
  const assignedTo = String(assignedToRaw || "");
  if (assignedTo && assignedTo === userId) return true;

  const assignedUsers = Array.isArray(werkbon?.assignedUsers) ? werkbon.assignedUsers : [];
  return assignedUsers.some((entry) => String(entry?._id || entry || "") === userId);
}

function getAllowedHourTypeIdsFromWerkbon(werkbon) {
  return Array.isArray(werkbon?.workorderTypeConfig?.allowedHourTypeIds)
    ? werkbon.workorderTypeConfig.allowedHourTypeIds
        .map((item) => String(item || "").trim())
        .filter(Boolean)
    : [];
}

function getPreferredHourTypeIdForWerkbon(werkbon) {
  return String(
    werkbon?.completionState?.defaultHourTypeId ||
      werkbon?.completionFlow?.defaultHourTypeId ||
      werkbon?.workorderTypeConfig?.defaultHourTypeId ||
      ""
  ).trim();
}

function resolveHourTypeOptionsForWerkbon(werkbon, options) {
  const source = Array.isArray(options) ? options : [];
  const allowedIds = getAllowedHourTypeIdsFromWerkbon(werkbon);
  if (allowedIds.length === 0) return source;
  return source.filter((item) => allowedIds.includes(String(item?._id || "").trim()));
}

function buildHourEntriesFingerprint(entries) {
  const list = Array.isArray(entries) ? entries : [];
  return JSON.stringify(
    list.map((entry) => ({
      hourTypeId: String(entry?.hourTypeId || ""),
      hourTypeName: String(entry?.hourTypeName || ""),
      description: String(entry?.description || "").trim(),
      hours: Number(entry?.hours || 0),
    }))
  );
}

function normalizeCustomerApprovalDraft(value = {}) {
  return {
    signedByName: String(value?.signedByName || "").trimStart(),
    signedByRole: String(value?.signedByRole || "").trimStart(),
    approved: value?.approved === true,
    signatureDataUrl: String(value?.signatureDataUrl || "").trim(),
  };
}

function hasCustomerApprovalInput(value = {}) {
  const draft = normalizeCustomerApprovalDraft(value);
  return Boolean(
    draft.signedByName ||
      draft.signedByRole ||
      draft.approved === true ||
      draft.signatureDataUrl
  );
}

function isSupportedSignatureDataUrl(value) {
  return (
    value.startsWith("data:image/png;base64,") ||
    value.startsWith("data:image/jpeg;base64,")
  );
}

function getCustomerApprovalValidationMessage(value = {}, { required = false } = {}) {
  const draft = normalizeCustomerApprovalDraft(value);
  const used = hasCustomerApprovalInput(draft);

  if (!used) {
    return required ? "Klant-handtekening is verplicht voor dit werkbontype." : "";
  }
  if (!draft.signedByName.trim()) {
    return "Naam van ondertekenaar is verplicht bij klant-akkoord";
  }
  if (draft.approved !== true) {
    return "Akkoord met uitgevoerde werkzaamheden is verplicht bij klant-handtekening";
  }
  if (!draft.signatureDataUrl) {
    return "Handtekening ontbreekt bij klant-akkoord";
  }
  if (!isSupportedSignatureDataUrl(draft.signatureDataUrl)) {
    return "Handtekening moet een PNG of JPEG data URL zijn";
  }
  if (draft.signatureDataUrl.length > SIGNATURE_DATA_URL_MAX_LENGTH) {
    return "signatureDataUrl is te groot";
  }
  return "";
}

export default function WerkbonView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { werkbonId } = useParams();
  const { user } = useUser();
  const monteurScopeKey = useMemo(() => createMonteurScopeKey(user), [user]);

  const [werkbonnen, setWerkbonnen] = useState([]);
  const [werkbonnenLoading, setWerkbonnenLoading] = useState(true);
  const [werkbonnenError, setWerkbonnenError] = useState("");

  const [activeWerkbonId, setActiveWerkbonId] = useState(werkbonId || "");
  const [markedWerkbonId, setMarkedWerkbonId] = useState("");
  const [werkbon, setWerkbon] = useState(null);
  const [werkbonLoading, setWerkbonLoading] = useState(true);
  const [werkbonError, setWerkbonError] = useState("");

  const [agendaItems, setAgendaItems] = useState([]);
  const [agendaLoading, setAgendaLoading] = useState(true);
  const [agendaError, setAgendaError] = useState("");
  const [agendaDate, setAgendaDate] = useState(new Date());
  const [agendaView, setAgendaView] = useState("day");

  const [hourEntries, setHourEntries] = useState([]);
  const [savedHourEntriesFingerprint, setSavedHourEntriesFingerprint] = useState(
    buildHourEntriesFingerprint([])
  );
  const [hourTypeOptions, setHourTypeOptions] = useState([]);
  const [materialEntries, setMaterialEntries] = useState([]);
  const [autoTimeBaseEntries, setAutoTimeBaseEntries] = useState([]);
  const [autoAdjustments, setAutoAdjustments] = useState([]);
  const [hourDraft, setHourDraft] = useState(EMPTY_HOUR);
  const [materialDraft, setMaterialDraft] = useState(EMPTY_MATERIAL);
  const [materialSuggestions, setMaterialSuggestions] = useState({
    recent: [],
    projectOrdered: [],
  });
  const [materialSuggestionsLoading, setMaterialSuggestionsLoading] = useState(false);
  const [materialSuggestionsError, setMaterialSuggestionsError] = useState("");
  const [materialSuggestionSearch, setMaterialSuggestionSearch] = useState("");
  const [adjustmentDraft, setAdjustmentDraft] = useState(EMPTY_ADJUSTMENT);
  const [entriesSaving, setEntriesSaving] = useState(false);
  const [timeSubmitLoading, setTimeSubmitLoading] = useState(false);
  const [tuPunchoutLoading, setTuPunchoutLoading] = useState(false);
  const [tuPunchoutError, setTuPunchoutError] = useState("");
  const [tuPunchoutSuccess, setTuPunchoutSuccess] = useState("");
  const [workorderPdfLoading, setWorkorderPdfLoading] = useState(false);

  const [activeTab, setActiveTab] = useState("werkbon");
  const [meldingText, setMeldingText] = useState("");
  const [meldingType, setMeldingType] = useState("unsafe_situation");
  const [meldingSeverity, setMeldingSeverity] = useState("medium");
  const [meldingPhoto, setMeldingPhoto] = useState(null);
  const [meldingSaving, setMeldingSaving] = useState(false);
  const [meldingError, setMeldingError] = useState("");
  const [meldingSuccess, setMeldingSuccess] = useState("");
  const [veiligheidsMeldingen, setVeiligheidsMeldingen] = useState([]);
  const [dailyLogForm, setDailyLogForm] = useState({
    description: "",
    blocked: false,
    blockReason: "",
  });
  const [dailyLogSaving, setDailyLogSaving] = useState(false);
  const [dailyLogError, setDailyLogError] = useState("");
  const [dailyLogSuccess, setDailyLogSuccess] = useState("");
  const [actieLoading, setActieLoading] = useState(false);
  const [showStopReasonPicker, setShowStopReasonPicker] = useState(false);
  const [lastStopReason, setLastStopReason] = useState(null);
  const [taakDraft, setTaakDraft] = useState("");
  const [actieTypeDraft, setActieTypeDraft] = useState("voorbereiding");
  const [taakSaving, setTaakSaving] = useState(false);
  const [taakError, setTaakError] = useState("");
  const [showTaakForm, setShowTaakForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState(() =>
    resolveStatusFilterFromSearch(location.search)
  );
  const [werkbonSearch, setWerkbonSearch] = useState("");
  const [photoCategoryDraft, setPhotoCategoryDraft] = useState("bewijs");
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoDeletingId, setPhotoDeletingId] = useState("");
  const [photoUploadProgress, setPhotoUploadProgress] = useState(0);
  const [photoError, setPhotoError] = useState("");
  const [photoSuccess, setPhotoSuccess] = useState("");
  const [photoEditingId, setPhotoEditingId] = useState("");
  const [workorderDocuments, setWorkorderDocuments] = useState({
    sharedDocuments: [],
    projectDocuments: [],
    workorderDocuments: [],
  });
  const [workorderDocumentsLoading, setWorkorderDocumentsLoading] = useState(false);
  const [projectWorkHistory, setProjectWorkHistory] = useState({
    projectNummer: "",
    items: [],
  });
  const [projectWorkHistoryLoading, setProjectWorkHistoryLoading] = useState(false);
  const [projectWorkHistoryError, setProjectWorkHistoryError] = useState("");
  const [timeExtensionRequests, setTimeExtensionRequests] = useState([]);
  const [timeExtensionLoading, setTimeExtensionLoading] = useState(false);
  const [timeExtensionError, setTimeExtensionError] = useState("");
  const [timeExtensionSuccess, setTimeExtensionSuccess] = useState("");
  const [timeExtensionSubmitting, setTimeExtensionSubmitting] = useState(false);
  const [showTimeExtensionModal, setShowTimeExtensionModal] = useState(false);
  const [customerApprovalDraft, setCustomerApprovalDraft] = useState(
    EMPTY_CUSTOMER_APPROVAL
  );
  const [customerApprovalError, setCustomerApprovalError] = useState("");
  const [showMobileWerkbonSheet, setShowMobileWerkbonSheet] = useState(false);
  const [doneListRange, setDoneListRange] = useState("all");
  const selectedPhotosRef = useRef([]);
  const markedWerkbonIdRef = useRef("");
  const activeMarkerStorageKey = useMemo(() => {
    const identifier =
      user?.sub || user?.userId || user?.id || user?.email || "default";
    return `logboek24.monteur.activeWerkbon.${identifier}`;
  }, [user?.sub, user?.userId, user?.id, user?.email]);
  const stopReasonStorageKey = useMemo(() => {
    const identifier =
      user?.sub || user?.userId || user?.id || user?.email || "default";
    return `logboek24.monteur.lastStopReason.${identifier}`;
  }, [user?.sub, user?.userId, user?.id, user?.email]);
  const withCurrentSearch = React.useCallback(
    (pathname) => {
      const search = String(location.search || "").trim();
      return search ? `${pathname}${search}` : pathname;
    },
    [location.search]
  );

  useEffect(() => {
    setStatusFilter(resolveStatusFilterFromSearch(location.search));
  }, [location.search]);

  useEffect(() => {
    setActiveWerkbonId(werkbonId || "");
  }, [werkbonId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const importStatus = String(
      params.get("ociImport") || params.get("tuImport") || ""
    )
      .trim()
      .toLowerCase();

    if (!importStatus) return;

    const importedCount = Number(
      params.get("ociImportedCount") || params.get("tuImportedCount") || 0
    );
    const errorMessage = String(
      params.get("ociError") || params.get("tuError") || ""
    ).trim();

    setActiveTab("werkbon");
    setTuPunchoutError("");
    setTuPunchoutSuccess("");

    if (importStatus === "success") {
      setTuPunchoutSuccess(
        importedCount > 0
          ? `${importedCount} producten succesvol geimporteerd.`
          : "Producten succesvol geimporteerd."
      );
    } else {
      setTuPunchoutError(errorMessage || "Bestelling niet afgerond, probeer opnieuw");
    }

    params.delete("ociImport");
    params.delete("ociImportedCount");
    params.delete("ociError");
    params.delete("tuImport");
    params.delete("tuImportedCount");
    params.delete("tuError");
    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(activeMarkerStorageKey);
      setMarkedWerkbonId(String(saved || ""));
    } catch {
      setMarkedWerkbonId("");
    }
  }, [activeMarkerStorageKey]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(stopReasonStorageKey);
      if (!saved) {
        setLastStopReason(null);
        return;
      }
      const parsed = JSON.parse(saved);
      const known = STOP_REASON_OPTIONS.find(
        (item) => item.code === String(parsed?.code || "").trim().toLowerCase()
      );
      setLastStopReason(known || null);
    } catch {
      setLastStopReason(null);
    }
  }, [stopReasonStorageKey]);

  useEffect(() => {
    markedWerkbonIdRef.current = markedWerkbonId;
  }, [markedWerkbonId]);

  useEffect(() => {
    try {
      if (!markedWerkbonId) {
        localStorage.removeItem(activeMarkerStorageKey);
      } else {
        localStorage.setItem(activeMarkerStorageKey, markedWerkbonId);
      }
    } catch {
      // ignore localStorage failures
    }
  }, [markedWerkbonId, activeMarkerStorageKey]);

  useEffect(() => {
    function applyWerkbonnenList(list) {
      setWerkbonnen(list);

      if (!werkbonId && list.length > 0) {
        const requestedFilter = resolveStatusFilterFromSearch(location.search);
        const filteredList =
          requestedFilter === "done"
            ? list.filter((item) => isWerkbonDoneStatus(item?.status))
            : requestedFilter === "open"
            ? list.filter((item) => !isWerkbonDoneStatus(item?.status))
            : list;
        const sourceList = filteredList.length > 0 ? filteredList : list;
        const today = toISODate(new Date());
        const firstToday = sourceList.find((item) => toISODate(item?.plannedDate) === today);
        const nextId = (firstToday || sourceList[0])._id;
        setActiveWerkbonId(nextId);
        if (!markedWerkbonIdRef.current && firstToday?._id) {
          setMarkedWerkbonId(String(firstToday._id));
        }
        navigate(withCurrentSearch(`/monteur/werkbonnen/${nextId}`));
      }
    }

    async function fetchWerkbonnen() {
      setWerkbonnenLoading(true);
      setWerkbonnenError("");
      try {
        const normalizedSearch = String(werkbonSearch || "").trim();
        const res = await api.get("/werkbonnen/mine", {
          params: normalizedSearch ? { search: normalizedSearch } : undefined,
        });
        const list = Array.isArray(res.data) ? res.data : [];
        applyWerkbonnenList(list);
        if (!normalizedSearch) {
          await setCachedWorkorderCollection({
            scopeKey: monteurScopeKey,
            cacheKey: "mine",
            payload: list,
          });
        }
      } catch (err) {
        if (String(werkbonSearch || "").trim()) {
          setWerkbonnenError(
            err?.response?.data?.message || "Kon zoekresultaten niet laden"
          );
          return;
        }
        const cached = await getCachedWorkorderCollection({
          scopeKey: monteurScopeKey,
          cacheKey: "mine",
        });
        const cachedList = Array.isArray(cached?.payload) ? cached.payload : [];

        if (cachedList.length > 0) {
          applyWerkbonnenList(cachedList);
          setWerkbonnenError("");
        } else {
          setWerkbonnenError(
            err?.response?.data?.message || "Kon werkbonnen niet laden"
          );
        }
      } finally {
        setWerkbonnenLoading(false);
      }
    }

    fetchWerkbonnen();
  }, [
    monteurScopeKey,
    werkbonId,
    location.search,
    navigate,
    werkbonSearch,
    withCurrentSearch,
  ]);

  useEffect(() => {
    if (!activeWerkbonId) {
      setWerkbon(null);
      setHourEntries([]);
      setSavedHourEntriesFingerprint(buildHourEntriesFingerprint([]));
      setHourTypeOptions([]);
      setMaterialEntries([]);
      setMaterialSuggestions({ recent: [], projectOrdered: [] });
      setMaterialSuggestionsError("");
      setAutoTimeBaseEntries([]);
      setAutoAdjustments([]);
      setWerkbonLoading(false);
      return;
    }

    async function fetchWerkbon() {
      setWerkbonLoading(true);
      setWerkbonError("");
      try {
        const [wbRes, finRes, hourTypeRes] = await Promise.all([
          api.get(`/werkbonnen/${activeWerkbonId}`),
          api.get(`/werkbonnen/${activeWerkbonId}/financial`),
          api.get("/tenant-hour-types/options"),
        ]);
        const wb = wbRes.data || null;
        setWerkbon(wb);
        const options = resolveHourTypeOptionsForWerkbon(
          wb,
          Array.isArray(hourTypeRes?.data) ? hourTypeRes.data : []
        );
        const preferredHourTypeId = getPreferredHourTypeIdForWerkbon(wb);
        setHourTypeOptions(options);
        setHourDraft((prev) => {
          const currentSelected = String(prev?.hourTypeId || "").trim();
          const stillExists = options.some(
            (item) => String(item?._id || "") === currentSelected
          );
          if (stillExists) return prev;
          return {
            ...prev,
            hourTypeId:
              (preferredHourTypeId &&
                options.some((item) => String(item?._id || "") === preferredHourTypeId) &&
                preferredHourTypeId) ||
              String(options[0]?._id || ""),
          };
        });
        const nextHourEntries = Array.isArray(finRes?.data?.hourEntries)
          ? finRes.data.hourEntries
          : [];
        setHourEntries(nextHourEntries);
        setSavedHourEntriesFingerprint(buildHourEntriesFingerprint(nextHourEntries));
        setMaterialEntries(
          Array.isArray(finRes?.data?.materialEntries) ? finRes.data.materialEntries : []
        );
        setAutoTimeBaseEntries(
          Array.isArray(finRes?.data?.autoTimeBaseEntries) ? finRes.data.autoTimeBaseEntries : []
        );
        setAutoAdjustments(
          Array.isArray(finRes?.data?.autoAdjustments) ? finRes.data.autoAdjustments : []
        );
        await Promise.all([
          setCachedWorkorderDetail({
            scopeKey: monteurScopeKey,
            workorderId: activeWerkbonId,
            payload: wb,
          }),
          setCachedWorkorderFinancial({
            scopeKey: monteurScopeKey,
            workorderId: activeWerkbonId,
            payload: finRes?.data || {},
          }),
          setCachedReferenceData({
            scopeKey: monteurScopeKey,
            type: "tenant-hour-types-options",
            payload: options,
          }),
        ]);
      } catch (err) {
        const [cachedWerkbon, cachedFinancial, cachedHourTypes] = await Promise.all([
          getCachedWorkorderDetail({
            scopeKey: monteurScopeKey,
            workorderId: activeWerkbonId,
          }),
          getCachedWorkorderFinancial({
            scopeKey: monteurScopeKey,
            workorderId: activeWerkbonId,
          }),
          getCachedReferenceData({
            scopeKey: monteurScopeKey,
            type: "tenant-hour-types-options",
          }),
        ]);

        const cachedWerkbonPayload = cachedWerkbon?.payload || null;
        const cachedFinancialPayload = cachedFinancial?.payload || {};
        const cachedHourTypeOptions = Array.isArray(cachedHourTypes?.payload)
          ? cachedHourTypes.payload
          : [];
        const resolvedCachedHourTypeOptions = resolveHourTypeOptionsForWerkbon(
          cachedWerkbonPayload,
          cachedHourTypeOptions
        );
        const preferredHourTypeId = getPreferredHourTypeIdForWerkbon(cachedWerkbonPayload);

        if (cachedWerkbonPayload) {
          setWerkbon(cachedWerkbonPayload);
          setHourTypeOptions(resolvedCachedHourTypeOptions);
          setHourDraft((prev) => {
            const currentSelected = String(prev?.hourTypeId || "").trim();
            const stillExists = resolvedCachedHourTypeOptions.some(
              (item) => String(item?._id || "") === currentSelected
            );
            if (stillExists) return prev;
            return {
              ...prev,
              hourTypeId:
                (preferredHourTypeId &&
                  resolvedCachedHourTypeOptions.some(
                    (item) => String(item?._id || "") === preferredHourTypeId
                  ) &&
                  preferredHourTypeId) ||
                String(resolvedCachedHourTypeOptions[0]?._id || ""),
            };
          });
          const nextHourEntries = Array.isArray(cachedFinancialPayload?.hourEntries)
            ? cachedFinancialPayload.hourEntries
            : [];
          setHourEntries(nextHourEntries);
          setSavedHourEntriesFingerprint(buildHourEntriesFingerprint(nextHourEntries));
          setMaterialEntries(
            Array.isArray(cachedFinancialPayload?.materialEntries)
              ? cachedFinancialPayload.materialEntries
              : []
          );
          setAutoTimeBaseEntries(
            Array.isArray(cachedFinancialPayload?.autoTimeBaseEntries)
              ? cachedFinancialPayload.autoTimeBaseEntries
              : []
          );
          setAutoAdjustments(
            Array.isArray(cachedFinancialPayload?.autoAdjustments)
              ? cachedFinancialPayload.autoAdjustments
              : []
          );
          setWerkbonError("");
        } else {
          setWerkbonError(
            err?.response?.data?.message || "Kon werkbon niet laden"
          );
        }
      } finally {
        setWerkbonLoading(false);
      }
    }

    fetchWerkbon();
  }, [activeWerkbonId, monteurScopeKey]);

  useEffect(() => {
    if (!activeWerkbonId) {
      setMaterialSuggestions({ recent: [], projectOrdered: [] });
      setMaterialSuggestionsError("");
      return;
    }

    let cancelled = false;
    async function fetchMaterialSuggestions() {
      try {
        setMaterialSuggestionsLoading(true);
        setMaterialSuggestionsError("");
        const res = await api.get(`/workorders/${activeWerkbonId}/material-suggestions`);
        if (cancelled) return;
        setMaterialSuggestions({
          recent: Array.isArray(res?.data?.recent) ? res.data.recent : [],
          projectOrdered: Array.isArray(res?.data?.projectOrdered)
            ? res.data.projectOrdered
            : [],
        });
      } catch (err) {
        if (cancelled) return;
        setMaterialSuggestions({ recent: [], projectOrdered: [] });
        setMaterialSuggestionsError(
          err?.response?.data?.message || "Materiaalsuggesties laden mislukt"
        );
      } finally {
        if (!cancelled) setMaterialSuggestionsLoading(false);
      }
    }

    fetchMaterialSuggestions();
    return () => {
      cancelled = true;
    };
  }, [activeWerkbonId]);

  useEffect(() => {
    if (!activeWerkbonId) {
      setWorkorderDocuments({
        sharedDocuments: [],
        projectDocuments: [],
        workorderDocuments: [],
      });
      return;
    }

    async function fetchWorkorderDocuments() {
      try {
        setWorkorderDocumentsLoading(true);
        const data = await getWorkorderDocuments(activeWerkbonId);
        setWorkorderDocuments(data);
      } catch {
        setWorkorderDocuments({
          sharedDocuments: [],
          projectDocuments: [],
          workorderDocuments: [],
        });
      } finally {
        setWorkorderDocumentsLoading(false);
      }
    }

    fetchWorkorderDocuments();
  }, [activeWerkbonId]);

  useEffect(() => {
    if (!activeWerkbonId) {
      setProjectWorkHistory({ projectNummer: "", items: [] });
      setProjectWorkHistoryError("");
      setProjectWorkHistoryLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchProjectWorkHistory() {
      try {
        setProjectWorkHistoryLoading(true);
        setProjectWorkHistoryError("");
        const res = await api.get(`/werkbonnen/${activeWerkbonId}/project-history`);
        if (cancelled) return;
        const data = res?.data || {};
        setProjectWorkHistory({
          projectNummer: String(data?.projectNummer || "").trim(),
          items: Array.isArray(data?.items) ? data.items : [],
        });
      } catch (err) {
        if (cancelled) return;
        setProjectWorkHistory({ projectNummer: "", items: [] });
        setProjectWorkHistoryError(
          err?.response?.data?.message || "Projecthistorie laden mislukt"
        );
      } finally {
        if (!cancelled) {
          setProjectWorkHistoryLoading(false);
        }
      }
    }

    fetchProjectWorkHistory();
    return () => {
      cancelled = true;
    };
  }, [activeWerkbonId]);

  useEffect(() => {
    if (!activeWerkbonId) {
      setTimeExtensionRequests([]);
      return;
    }

    async function fetchTimeExtensionRequests() {
      try {
        setTimeExtensionLoading(true);
        setTimeExtensionError("");
        const res = await api.get("/time-extension-requests", {
          params: { workOrderId: activeWerkbonId },
        });
        setTimeExtensionRequests(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        setTimeExtensionError(
          err?.response?.data?.message || "Extra tijd verzoeken laden mislukt"
        );
      } finally {
        setTimeExtensionLoading(false);
      }
    }

    fetchTimeExtensionRequests();
  }, [activeWerkbonId]);

  useEffect(() => {
    if (!activeWerkbonId) {
      setVeiligheidsMeldingen([]);
      return;
    }

    let cancelled = false;
    async function fetchSafetyReports() {
      try {
        const res = await api.get("/kam/reports", {
          params: {
            workorderId: activeWerkbonId,
            mine: 1,
            limit: 25,
            sortBy: "createdAt",
            sortDir: "desc",
          },
        });
        if (cancelled) return;
        setVeiligheidsMeldingen(Array.isArray(res?.data?.items) ? res.data.items : []);
      } catch {
        if (cancelled) return;
        setVeiligheidsMeldingen([]);
      }
    }

    fetchSafetyReports();
    return () => {
      cancelled = true;
    };
  }, [activeWerkbonId]);

  useEffect(() => {
    setShowTaakForm(false);
    setActiveTab("werkbon");
    setTaakDraft("");
    setActieTypeDraft("voorbereiding");
    setTaakError("");
    setDailyLogError("");
    setDailyLogSuccess("");
    setShowStopReasonPicker(false);
    setSelectedPhotos((prev) => {
      revokePreviewUrls(prev);
      return [];
    });
    setPhotoCategoryDraft("bewijs");
    setPhotoUploadProgress(0);
    setPhotoError("");
    setPhotoSuccess("");
    setMeldingText("");
    setMeldingType("unsafe_situation");
    setMeldingSeverity("medium");
    setMeldingPhoto(null);
    setMeldingError("");
    setMeldingSuccess("");
    setShowTimeExtensionModal(false);
    setTimeExtensionError("");
    setTimeExtensionSuccess("");
    setTuPunchoutError("");
    setTuPunchoutSuccess("");
    setMaterialSuggestionSearch("");
    setCustomerApprovalDraft(EMPTY_CUSTOMER_APPROVAL);
    setCustomerApprovalError("");
  }, [activeWerkbonId]);

  useEffect(() => {
    if (
      werkbon?.customerApproval &&
      hasCustomerApprovalInput(werkbon.customerApproval)
    ) {
      setCustomerApprovalDraft(
        normalizeCustomerApprovalDraft(werkbon.customerApproval)
      );
      setCustomerApprovalError("");
    }
  }, [werkbon?.customerApproval]);

  useEffect(() => {
    selectedPhotosRef.current = selectedPhotos;
  }, [selectedPhotos]);

  useEffect(() => {
    return () => {
      revokePreviewUrls(selectedPhotosRef.current);
    };
  }, []);

  useEffect(() => {
    let timerId;

    function scheduleNextMidnight() {
      const now = new Date();
      const nextMidnight = new Date(now);
      nextMidnight.setHours(24, 0, 0, 0);

      timerId = window.setTimeout(() => {
        // Jump to the new day at local midnight so tomorrow's workorders are ready.
        setAgendaDate(new Date());
        scheduleNextMidnight();
      }, nextMidnight.getTime() - now.getTime());
    }

    scheduleNextMidnight();

    return () => {
      if (timerId) window.clearTimeout(timerId);
    };
  }, []);

  useEffect(() => {
    async function fetchAgenda() {
      setAgendaLoading(true);
      setAgendaError("");

      try {
        const { payload } = await getAgendaWithOfflineCache({
          scopeKey: monteurScopeKey,
          view: agendaView,
          date: toISODate(agendaDate),
        });
        const data = payload || {};
        const werkbonnen = Array.isArray(data.werkbonnen)
          ? data.werkbonnen
          : Array.isArray(payload)
          ? payload
          : [];
        setAgendaItems(werkbonnen);
      } catch (err) {
        setAgendaError(
          err?.response?.data?.message || "Kon agenda niet laden"
        );
      } finally {
        setAgendaLoading(false);
      }
    }

    fetchAgenda();
  }, [agendaDate, agendaView, monteurScopeKey]);

  const currentTaken = useMemo(() => {
    if (!werkbon) return [];
    const acties = (werkbon.acties || []).map((item) => ({
      id: item?._id || "",
      type: formatActionTypeLabel(item?.type),
      status: formatActionStatusLabel(item?.status),
      text: item?.title || "-",
    }));
    const binnen = (werkbon.scope?.binnen || []).map((item) => ({
      type: "Binnen scope",
      text: item,
    }));
    const buiten = (werkbon.scope?.buiten || []).map((item) => ({
      type: "Buiten scope",
      text: item,
    }));
    const legacyWerkzaamheden = acties.length
      ? []
      : (werkbon.uitTeVoerenWerkzaamheden || []).map((item) => {
          const parsed = parseActionText(item);
          return {
            id: "",
            type: parsed.type || "Actie",
            status: "Open",
            text: parsed.text,
          };
        });

    return [...acties, ...legacyWerkzaamheden, ...binnen, ...buiten];
  }, [werkbon]);

  const werkbonnenOpDag = useMemo(() => {
    return agendaItems
      .filter((item) => agendaItemOverlapsDay(item, agendaDate))
      .filter((item) => matchesStatusFilter(item?.status, statusFilter));
  }, [agendaItems, agendaDate, statusFilter]);

  const werkbonnenInAgendaView = useMemo(() => {
    if (agendaView === "week") {
      return agendaItems.filter((item) => matchesStatusFilter(item?.status, statusFilter));
    }
    return werkbonnenOpDag;
  }, [agendaItems, agendaView, statusFilter, werkbonnenOpDag]);
  const mobileWerkbonOptions = useMemo(() => {
    return [...werkbonnen].sort((a, b) => {
      const dateA = new Date(a?.plannedDate || a?.createdAt || 0).getTime();
      const dateB = new Date(b?.plannedDate || b?.createdAt || 0).getTime();
      return dateB - dateA;
    });
  }, [werkbonnen]);
  const mobileCompletedCount = useMemo(() => {
    return mobileWerkbonOptions.filter((item) =>
      isWerkbonDoneStatus(item?.status)
    ).length;
  }, [mobileWerkbonOptions]);
  const doneWerkbonnen = useMemo(() => {
    return mobileWerkbonOptions.filter((item) => isWerkbonDoneStatus(item?.status));
  }, [mobileWerkbonOptions]);
  const filteredDoneWerkbonnen = useMemo(() => {
    return doneWerkbonnen.filter((item) => matchesDoneRange(item, doneListRange));
  }, [doneWerkbonnen, doneListRange]);

  const werkbonPhotos = useMemo(() => {
    return normalizeWerkbonPhotos(werkbon);
  }, [werkbon]);

  const highlightedSharedDocuments = useMemo(() => {
    return (workorderDocuments.sharedDocuments || []).filter((doc) => doc.important);
  }, [workorderDocuments]);

  const latestTimeExtensionRequest = useMemo(() => {
    if (!Array.isArray(timeExtensionRequests) || timeExtensionRequests.length === 0) {
      return null;
    }
    return timeExtensionRequests
      .slice()
      .sort((a, b) => {
        const timeA = new Date(a?.createdAt || 0).getTime();
        const timeB = new Date(b?.createdAt || 0).getTime();
        return timeB - timeA;
      })[0];
  }, [timeExtensionRequests]);

  const markedWerkbon = useMemo(() => {
    if (!markedWerkbonId) return null;
    const found = werkbonnen.find(
      (item) => String(item?._id || "") === String(markedWerkbonId)
    );
    if (found) return found;
    if (werkbon && String(werkbon?._id || "") === String(markedWerkbonId)) {
      return werkbon;
    }
    return null;
  }, [markedWerkbonId, werkbonnen, werkbon]);

  const activeTodayWerkbon = useMemo(() => {
    if (!markedWerkbon) return null;
    if (!isTodayDate(markedWerkbon?.plannedDate)) return null;
    return markedWerkbon;
  }, [markedWerkbon]);

  const currentDailyLog = useMemo(() => {
    return resolveTodayDailyLog(werkbon, user, DAILY_LOG_TIMEZONE);
  }, [werkbon, user]);

  useEffect(() => {
    setDailyLogForm({
      description: currentDailyLog?.description || "",
      blocked: Boolean(currentDailyLog?.blocked),
      blockReason: currentDailyLog?.blockReason || "",
    });
    setDailyLogError("");
    setDailyLogSuccess("");
  }, [activeWerkbonId, currentDailyLog]);

  useEffect(() => {
    if (!activeWerkbonId) return;
    const activeFromList = werkbonnen.find(
      (item) => String(item?._id || "") === String(activeWerkbonId)
    );
    const activeItem = activeFromList || (String(werkbon?._id || "") === String(activeWerkbonId) ? werkbon : null);
    if (!activeItem) return;
    if (!isTodayDate(activeItem?.plannedDate)) return;
    setMarkedWerkbonId(String(activeWerkbonId));
  }, [activeWerkbonId, werkbonnen, werkbon]);

  useEffect(() => {
    if (!markedWerkbonId) return;
    const exists = werkbonnen.some(
      (item) => String(item?._id || "") === String(markedWerkbonId)
    );
    if (!exists && String(werkbon?._id || "") !== String(markedWerkbonId)) {
      setMarkedWerkbonId("");
    }
  }, [werkbonnen, werkbon, markedWerkbonId]);

  useEffect(() => {
    if (!markedWerkbonId) return;
    if (!markedWerkbon || !isTodayDate(markedWerkbon?.plannedDate)) {
      setMarkedWerkbonId("");
    }
  }, [agendaDate, markedWerkbonId, markedWerkbon]);

  function openWerkbon(id, hintItem = null) {
    if (!id) return;

    const candidate =
      hintItem ||
      werkbonnen.find((item) => String(item?._id || "") === String(id)) ||
      agendaItems.find((item) => String(item?._id || "") === String(id)) ||
      null;

    const executableFrom = candidate?.executableFrom ? new Date(candidate.executableFrom) : null;
    const hasFutureGate =
      executableFrom instanceof Date &&
      !Number.isNaN(executableFrom.getTime()) &&
      executableFrom.getTime() > Date.now();
    const locked = candidate?.locked === true || hasFutureGate;

    if (locked) {
      const when = hasFutureGate
        ? executableFrom.toLocaleString("nl-NL", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          })
        : "later";
      setWerkbonError(`Werkbon is beschikbaar vanaf ${when}`);
      return;
    }

    setShowMobileWerkbonSheet(false);
    setWerkbonError("");
    setActiveWerkbonId(id);
    const selected = werkbonnen.find((item) => String(item?._id || "") === String(id));
    if (selected && isTodayDate(selected?.plannedDate)) {
      setMarkedWerkbonId(String(id));
    }
    navigate(withCurrentSearch(`/monteur/werkbonnen/${id}`));
  }

  async function refreshActiveWerkbon() {
    if (!activeWerkbonId) return;
    const res = await api.get(`/werkbonnen/${activeWerkbonId}`);
    setWerkbon(res.data || null);
  }

  async function handleStartWerkbon() {
    if (!activeWerkbonId) return;
    setActieLoading(true);
    try {
      await api.post(`/werkbonnen/${activeWerkbonId}/start`);
      await refreshActiveWerkbon();
    } catch (err) {
      setWerkbonError(
        err?.response?.data?.message || "Starten van werkbon is mislukt"
      );
    } finally {
      setActieLoading(false);
    }
  }

  async function handleSelfAssignWerkbon() {
    if (!activeWerkbonId) return;
    setActieLoading(true);
    setWerkbonError("");
    try {
      await api.post(`/werkbonnen/${activeWerkbonId}/self-assign`);
      await refreshActiveWerkbon();
    } catch (err) {
      setWerkbonError(
        err?.response?.data?.message || "Zelf inplannen mislukt"
      );
    } finally {
      setActieLoading(false);
    }
  }

  function openStopReasonPicker() {
    setWerkbonError("");
    setShowStopReasonPicker(true);
  }

  async function handleBlockWerkbon(reasonOption) {
    if (!activeWerkbonId) return;

    const resolvedReason =
      reasonOption && reasonOption.code && reasonOption.text
        ? reasonOption
        : null;
    if (!resolvedReason) {
      setWerkbonError("Kies eerst een stopreden.");
      return;
    }

    setActieLoading(true);
    setWerkbonError("");
    try {
      await api.post(`/werkbonnen/${activeWerkbonId}/block`, {
        blockedReasonCode: resolvedReason.code,
        blockedReasonText: resolvedReason.text,
        reason: "monteur_block",
      });
      setLastStopReason(resolvedReason);
      try {
        localStorage.setItem(stopReasonStorageKey, JSON.stringify(resolvedReason));
      } catch {
        // ignore localStorage failures
      }
      setShowStopReasonPicker(false);
      await refreshActiveWerkbon();
    } catch (err) {
      setWerkbonError(
        err?.response?.data?.message || "Blokkeren van werkbon is mislukt"
      );
    } finally {
      setActieLoading(false);
    }
  }

  async function handleUnblockWerkbon() {
    if (!activeWerkbonId) return;
    setActieLoading(true);
    setWerkbonError("");
    try {
      await api.post(`/werkbonnen/${activeWerkbonId}/unblock`, {
        reason: "monteur_resume",
      });
      await refreshActiveWerkbon();
    } catch (err) {
      setWerkbonError(
        err?.response?.data?.message || "Hervatten van werkbon is mislukt"
      );
    } finally {
      setActieLoading(false);
    }
  }

  async function handleAfrondenWerkbon() {
    if (!activeWerkbonId) return;
    const effectiveUiPolicy =
      werkbon?.effectiveUiPolicy && typeof werkbon.effectiveUiPolicy === "object"
        ? werkbon.effectiveUiPolicy
        : {};
    const signatureRequired = effectiveUiPolicy.requireSignature === true;
    const signatureEditable = effectiveUiPolicy.editSignature !== false;
    const hoursRequired = werkbon?.completionFlow?.requireHours !== false;
    const signatureCaptured =
      werkbon?.completionFlow?.signatureCaptured === true ||
      werkbon?.completionChecks?.signatureCaptured === true ||
      werkbon?.akkoord?.gegeven === true;
    const normalizedCustomerApproval = normalizeCustomerApprovalDraft(customerApprovalDraft);
    const customerApprovalUsed = hasCustomerApprovalInput(normalizedCustomerApproval);
    const customerApprovalValidationMessage = getCustomerApprovalValidationMessage(
      normalizedCustomerApproval,
      { required: signatureRequired && !signatureCaptured }
    );
    const customerApprovalReady = !customerApprovalValidationMessage && customerApprovalUsed;

    setCustomerApprovalError("");
    if (signatureRequired && !signatureCaptured && !signatureEditable) {
      const message =
        "Klant-handtekening is verplicht voor deze werkbon, maar staat in deze rol op alleen-lezen.";
      setCustomerApprovalError(message);
      setWerkbonError(message);
      return;
    }
    if (customerApprovalValidationMessage) {
      setCustomerApprovalError(customerApprovalValidationMessage);
      setWerkbonError(customerApprovalValidationMessage);
      return;
    }

    setActieLoading(true);
    try {
      if (hourEntriesDirty) {
        try {
          await saveEntries({ throwOnError: true });
        } catch (err) {
          if (hoursRequired) {
            setWerkbonError(
              "Afronden geblokkeerd: uurregels konden niet worden opgeslagen. Sla eerst uren op en probeer opnieuw."
            );
            return;
          }
        }
      }

      async function attempt(payload) {
        try {
          await api.post(`/werkbonnen/${activeWerkbonId}/afronden`, payload);
          await refreshActiveWerkbon();
          setWerkbonError("");
          setCustomerApprovalError("");
        } catch (err) {
          const message =
            err?.response?.data?.message || "Afronden van werkbon is mislukt";

          if (message.includes("confirmZeroHours=true")) {
            const confirmed = window.confirm(
              "Er zijn nog geen uren geregistreerd. Wil je deze werkbon afronden met expliciet 0 uur?"
            );
            if (confirmed) {
              return attempt({ ...payload, confirmZeroHours: true });
            }
          }

          if (message.includes("confirmNoMaterial=true")) {
            const confirmed = window.confirm(
              "Er is geen materiaal geregistreerd. Wil je expliciet bevestigen dat er geen materiaal is gebruikt?"
            );
            if (confirmed) {
              return attempt({ ...payload, confirmNoMaterial: true });
            }
          }

          if (message.includes("confirmNoEvidence=true")) {
            const confirmed = window.confirm(
              "Er zijn geen foto's of bijlagen toegevoegd. Wil je expliciet bevestigen dat bewijs niet nodig is?"
            );
            if (confirmed) {
              return attempt({ ...payload, confirmNoEvidence: true });
            }
          }

          setWerkbonError(message);
        }
      }

      const basePayload = {
        ...((customerApprovalReady || (signatureRequired && signatureCaptured))
          ? { signatureProvided: true }
          : {}),
        ...(customerApprovalReady
          ? {
              customerApproval: {
                signedByName: normalizedCustomerApproval.signedByName.trim(),
                signedByRole: normalizedCustomerApproval.signedByRole.trim(),
                approved: true,
                signatureDataUrl: normalizedCustomerApproval.signatureDataUrl,
              },
            }
          : {}),
      };
      await attempt(basePayload);
    } finally {
      setActieLoading(false);
    }
  }

  async function handleVeiligheidMelding(e) {
    e.preventDefault();
    if (!activeWerkbonId) return;

    const text = meldingText.trim();
    if (!text) {
      setMeldingError("Vul eerst een melding in.");
      return;
    }

    setMeldingSaving(true);
    setMeldingError("");
    setMeldingSuccess("");

    try {
      let photoIds = [];
      if (meldingPhoto) {
        const formData = new FormData();
        formData.append("workorderId", activeWerkbonId);
        formData.append("category", "bewijs");
        formData.append("photo", meldingPhoto, meldingPhoto.name);
        const photoRes = await api.post("/photos/upload", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        const photoId = String(photoRes?.data?._id || "").trim();
        if (photoId) photoIds = [photoId];
      }

      await api.post("/kam/reports", {
        title: text.slice(0, 120) || "Veiligheidsmelding",
        description: text,
        type: meldingType,
        severity: meldingSeverity,
        workorderId: activeWerkbonId,
        projectId: String(werkbon?.projectId?._id || werkbon?.projectId || "").trim() || undefined,
        clientId: String(werkbon?.clientId?._id || werkbon?.clientId || "").trim() || undefined,
        installationId:
          String(werkbon?.logbook?.installationId?._id || werkbon?.logbook?.installationId || "").trim() ||
          undefined,
        photoIds,
      });
      setMeldingText("");
      setMeldingPhoto(null);
      setMeldingSuccess("Melding opgeslagen.");

      const reportRes = await api.get("/kam/reports", {
        params: {
          workorderId: activeWerkbonId,
          mine: 1,
          limit: 25,
          sortBy: "createdAt",
          sortDir: "desc",
        },
      });
      setVeiligheidsMeldingen(Array.isArray(reportRes?.data?.items) ? reportRes.data.items : []);
    } catch (err) {
      setMeldingError(
        err?.response?.data?.message || "Veiligheidsmelding opslaan is mislukt"
      );
    } finally {
      setMeldingSaving(false);
    }
  }

  async function handleSubmitTimeExtensionRequest(payload) {
    if (!activeWerkbonId) return;

    try {
      setTimeExtensionSubmitting(true);
      setTimeExtensionError("");
      setTimeExtensionSuccess("");

      const res = await api.post("/time-extension-requests", {
        workOrderId: activeWerkbonId,
        requestedMinutes: payload.requestedMinutes,
        reason: payload.reason,
      });

      const created = res?.data || null;
      setTimeExtensionRequests((prev) => [created, ...(Array.isArray(prev) ? prev : [])]);
      setShowTimeExtensionModal(false);
      setTimeExtensionSuccess("Extra tijd verzoek verstuurd.");
    } catch (err) {
      setTimeExtensionError(
        err?.response?.data?.message || "Extra tijd verzoek versturen mislukt"
      );
    } finally {
      setTimeExtensionSubmitting(false);
    }
  }

  async function handleDaglogSubmit(e) {
    e.preventDefault();
    if (!activeWerkbonId) return;

    const description = String(dailyLogForm.description || "").trim();
    if (!description) {
      setDailyLogError("Vul een daglogbeschrijving in.");
      return;
    }

    setDailyLogSaving(true);
    setDailyLogError("");
    setDailyLogSuccess("");

    try {
      await api.put(`/werkbonnen/${activeWerkbonId}/daglog`, {
        description,
        blocked: false,
        blockReason: "",
      });

      const res = await api.get(`/werkbonnen/${activeWerkbonId}`);
      setWerkbon(res.data || null);
      setDailyLogSuccess("Daglog van vandaag opgeslagen.");
    } catch (err) {
      setDailyLogError(err?.response?.data?.message || "Daglog opslaan mislukt");
    } finally {
      setDailyLogSaving(false);
    }
  }

  async function handleAddTaak(e) {
    e.preventDefault();
    if (!activeWerkbonId) return;

    const text = String(taakDraft || "").trim();
    if (!text) {
      setTaakError("Vul eerst een actie in.");
      return;
    }
    setTaakSaving(true);
    setTaakError("");

    try {
      const res = await api.post(`/werkbonnen/${activeWerkbonId}/acties`, {
        title: text,
        type: actieTypeDraft,
      });
      const updatedWerkbon = res?.data?.werkbon || null;

      if (updatedWerkbon) {
        setWerkbon(updatedWerkbon);
      } else {
        const wbRes = await api.get(`/werkbonnen/${activeWerkbonId}`);
        setWerkbon(wbRes.data || null);
      }

      setTaakDraft("");
      setActieTypeDraft("voorbereiding");
      setShowTaakForm(false);
    } catch (err) {
      const status = err?.response?.status;
      const backendMessage = err?.response?.data?.message;

      if (backendMessage) {
        setTaakError(backendMessage);
      } else if (status === 403) {
        setTaakError(
          "Geen toegang om een actie toe te voegen. Koppel de monteur als uitvoerder of teamlid op deze werkbon."
        );
      } else if (status === 404) {
        setTaakError("Actie-endpoint niet gevonden. Herstart de backend en probeer opnieuw.");
      } else {
        setTaakError("Actie toevoegen is mislukt");
      }
    } finally {
      setTaakSaving(false);
    }
  }

  function handleSelectPhotos(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const additions = files.map((file) => ({
      id: createLocalId(),
      file,
      previewUrl: URL.createObjectURL(file),
    }));

    setSelectedPhotos((prev) => [...prev, ...additions]);
    setPhotoError("");
    setPhotoSuccess("");
    e.target.value = "";
  }

  async function handleEditSelectedPhoto(photoId) {
    const selected = selectedPhotos.find((item) => item.id === photoId);
    if (!selected) return;

    try {
      setPhotoEditingId(photoId);
      setPhotoError("");
      setPhotoSuccess("");

      const edited = await openDefaultEditor({
        src: selected.previewUrl,
        locale: locale_nl_NL,
        imageWriter: {
          mimeType: getPreferredOutputMimeType(selected.file?.type),
          quality: 0.8,
        },
      });

      if (!edited || !edited.dest) return;

      const editedFile = await editorDestToFile(edited.dest, selected.file.name);
      const nextPreviewUrl = URL.createObjectURL(editedFile);

      setSelectedPhotos((prev) =>
        prev.map((item) => {
          if (item.id !== photoId) return item;
          if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
          return {
            ...item,
            file: editedFile,
            previewUrl: nextPreviewUrl,
          };
        })
      );

      setPhotoSuccess("Foto bijgewerkt in editor.");
    } catch (err) {
      setPhotoError("Foto bewerken is mislukt.");
    } finally {
      setPhotoEditingId("");
    }
  }

  function removeSelectedPhoto(photoId) {
    setSelectedPhotos((prev) => {
      const next = [];
      for (const item of prev) {
        if (item.id === photoId) {
          if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
          continue;
        }
        next.push(item);
      }
      return next;
    });
  }

  async function handleUploadPhotos() {
    if (!activeWerkbonId) return;
    if (photoEditingId) {
      setPhotoError("Wacht tot de foto-bewerking klaar is.");
      return;
    }
    if (!selectedPhotos.length) {
      setPhotoError("Selecteer eerst minstens één foto.");
      return;
    }
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      setPhotoError("Geen internetverbinding. Probeer opnieuw zodra je weer online bent.");
      return;
    }

    setPhotoUploading(true);
    setPhotoUploadProgress(0);
    setPhotoError("");
    setPhotoSuccess("");

    try {
      const formData = new FormData();
      for (const item of selectedPhotos) {
        let uploadFile = item.file;
        try {
          uploadFile = await compressImageFile(item.file, {
            maxWidth: 1600,
            quality: 0.72,
            preferWebP: false,
            forceJpeg: true,
          });
        } catch {
          // Fallback: upload origineel bestand als client-side compressie faalt.
          uploadFile = item.file;
        }
        formData.append("fotos", uploadFile, uploadFile.name);
      }
      formData.append("category", photoCategoryDraft || "overig");

      const uploadRes = await api.post(`/werkbonnen/${activeWerkbonId}/fotos`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: PHOTO_UPLOAD_TIMEOUT_MS,
        onUploadProgress: (event) => {
          if (!event.total) return;
          const progress = Math.round((event.loaded / event.total) * 100);
          setPhotoUploadProgress(progress);
        },
      });

      const uploadedPhotos = Array.isArray(uploadRes?.data?.photos) ? uploadRes.data.photos : [];
      const uploadedFiles = Array.isArray(uploadRes?.data?.files) ? uploadRes.data.files : [];

      setWerkbon((prev) => {
        if (!prev) return prev;
        const nextLegacy = Array.isArray(prev.fotos) ? [...prev.fotos, ...uploadedFiles] : [...uploadedFiles];
        const nextModern = Array.isArray(prev.photos) ? [...prev.photos, ...uploadedPhotos] : [...uploadedPhotos];
        return {
          ...prev,
          fotos: nextLegacy,
          photos: nextModern,
        };
      });

      setSelectedPhotos((prev) => {
        revokePreviewUrls(prev);
        return [];
      });
      setPhotoUploadProgress(100);
      setPhotoSuccess("Foto's succesvol geüpload.");

      try {
        const res = await api.get(`/werkbonnen/${activeWerkbonId}`);
        setWerkbon(res.data || null);
      } catch (refreshErr) {
        console.warn("Werkbon refresh na foto-upload mislukt:", refreshErr);
      }
    } catch (err) {
      setPhotoUploadProgress(0);
      if (err?.code === "ECONNABORTED") {
        setPhotoError("Foto upload duurde te lang. Controleer je verbinding en probeer opnieuw.");
      } else if (err?.message === "Network Error") {
        setPhotoError("Netwerkfout tijdens upload. Controleer je verbinding en probeer opnieuw.");
      } else {
        setPhotoError(err?.response?.data?.message || "Foto upload mislukt");
      }
    } finally {
      setPhotoUploading(false);
    }
  }

  async function handleDeleteWerkbonPhoto(photoId) {
    if (!activeWerkbonId || !photoId) return;
    if (!window.confirm("Weet je zeker dat je deze foto wilt verwijderen?")) return;

    setPhotoDeletingId(photoId);
    setPhotoError("");
    setPhotoSuccess("");

    try {
      await api.delete(`/werkbonnen/${activeWerkbonId}/fotos/${photoId}`);

      setWerkbon((prev) => {
        if (!prev) return prev;

        const nextPhotos = Array.isArray(prev.photos)
          ? prev.photos.filter((photo) => String(photo?._id || "") !== String(photoId))
          : [];
        const remainingFilenames = new Set(
          nextPhotos.map((photo) => String(photo?.filename || "").trim()).filter(Boolean)
        );
        const nextFotos = Array.isArray(prev.fotos)
          ? prev.fotos.filter((value) => remainingFilenames.has(String(value || "").trim()))
          : [];

        return {
          ...prev,
          photos: nextPhotos,
          fotos: nextFotos,
        };
      });

      setPhotoSuccess("Foto verwijderd.");
    } catch (err) {
      setPhotoError(err?.response?.data?.message || "Foto verwijderen mislukt");
    } finally {
      setPhotoDeletingId("");
    }
  }

  function addHourDraft() {
    if (!hourDraft.hours || Number(hourDraft.hours) <= 0) return;
    const selectedHourType = hourTypeOptions.find(
      (item) => String(item?._id || "") === String(hourDraft.hourTypeId || "")
    );
    setHourEntries((prev) => [
      ...prev,
      {
        hourTypeId: String(selectedHourType?._id || hourDraft.hourTypeId || ""),
        hourTypeName: String(selectedHourType?.name || ""),
        description: hourDraft.description || "",
        hours: Number(hourDraft.hours),
      },
    ]);
    setHourDraft((prev) => ({
      ...EMPTY_HOUR,
      hourTypeId: String(prev?.hourTypeId || ""),
    }));
  }

  function addMaterialDraft() {
    if (!materialDraft.description || !materialDraft.qty) return;
    setMaterialEntries((prev) => [
      ...prev,
      {
        description: materialDraft.description,
        qty: Number(materialDraft.qty),
        unit: materialDraft.unit || "st",
        supplier: String(materialDraft.supplier || "").trim(),
        supplierArticleNumber: String(
          materialDraft.supplierArticleNumber || ""
        ).trim(),
        unitSell: Number(materialDraft.unitSell || 0),
        vatRate: Number(materialDraft.vatRate || 21),
      },
    ]);
    setMaterialDraft(EMPTY_MATERIAL);
  }

  function removeMaterialEntry(indexToRemove) {
    setMaterialEntries((prev) =>
      prev.filter((_, index) => Number(index) !== Number(indexToRemove))
    );
  }

  function applyMaterialSuggestion(suggestion) {
    setMaterialDraft((prev) => ({
      ...prev,
      description: String(suggestion?.description || ""),
      unit: String(suggestion?.unit || "st"),
      supplier: String(suggestion?.supplier || ""),
      supplierArticleNumber: String(
        suggestion?.supplierArticleNumber || suggestion?.articleCode || ""
      ),
      unitSell: Number.isFinite(Number(suggestion?.unitSell))
        ? String(Number(suggestion.unitSell))
        : prev.unitSell,
      vatRate: Number.isFinite(Number(suggestion?.vatRate))
        ? String(Number(suggestion.vatRate))
        : prev.vatRate,
      qty: prev.qty || "1",
    }));
  }

  function addAdjustmentDraft() {
    const hours = Number(adjustmentDraft.hours || 0);
    const reason = String(adjustmentDraft.reason || "").trim();
    if (!Number.isFinite(hours) || hours <= 0 || !reason) return;

    setAutoAdjustments((prev) => [
      ...prev,
      {
        category: adjustmentDraft.category || "overig",
        description: adjustmentDraft.description || "",
        hours,
        reason,
      },
    ]);
    setAdjustmentDraft(EMPTY_ADJUSTMENT);
  }

  async function saveEntries(options = {}) {
    const throwOnError = options?.throwOnError === true;
    if (!activeWerkbonId) return;
    setEntriesSaving(true);
    try {
      const autoMode = werkbon?.timeMode === "auto";
      if (!autoMode) {
        await api.put(`/werkbonnen/${activeWerkbonId}/hour-entries`, {
          hourEntries,
        });
      } else {
        await api.put(`/werkbonnen/${activeWerkbonId}/auto-adjustments`, {
          autoAdjustments,
        });
      }
      await api.put(`/werkbonnen/${activeWerkbonId}/material-entries`, {
        materialEntries,
      });
      const [wbRes, finRes] = await Promise.all([
        api.get(`/werkbonnen/${activeWerkbonId}`),
        api.get(`/werkbonnen/${activeWerkbonId}/financial`),
      ]);
      setWerkbon(wbRes.data || null);
      const nextHourEntries = Array.isArray(finRes?.data?.hourEntries)
        ? finRes.data.hourEntries
        : [];
      setHourEntries(nextHourEntries);
      setSavedHourEntriesFingerprint(buildHourEntriesFingerprint(nextHourEntries));
      setAutoTimeBaseEntries(
        Array.isArray(finRes?.data?.autoTimeBaseEntries) ? finRes.data.autoTimeBaseEntries : []
      );
      setAutoAdjustments(
        Array.isArray(finRes?.data?.autoAdjustments) ? finRes.data.autoAdjustments : []
      );
      setMaterialEntries(
        Array.isArray(finRes?.data?.materialEntries) ? finRes.data.materialEntries : []
      );
    } catch (err) {
      setWerkbonError(
        err?.response?.data?.message || "Opslaan van uren/materialen mislukt"
      );
      if (throwOnError) throw err;
    } finally {
      setEntriesSaving(false);
    }
  }

  async function submitAutoTimeForApproval() {
    if (!activeWerkbonId || !isAutoTimeMode) return;
    setTimeSubmitLoading(true);
    try {
      await api.post(`/werkbonnen/${activeWerkbonId}/time-approval/submit`);
      const [wbRes, finRes] = await Promise.all([
        api.get(`/werkbonnen/${activeWerkbonId}`),
        api.get(`/werkbonnen/${activeWerkbonId}/financial`),
      ]);
      setWerkbon(wbRes.data || null);
      setAutoTimeBaseEntries(
        Array.isArray(finRes?.data?.autoTimeBaseEntries) ? finRes.data.autoTimeBaseEntries : []
      );
      setAutoAdjustments(
        Array.isArray(finRes?.data?.autoAdjustments) ? finRes.data.autoAdjustments : []
      );
    } catch (err) {
      setWerkbonError(
        err?.response?.data?.message || "Uren indienen voor goedkeuring is mislukt"
      );
    } finally {
      setTimeSubmitLoading(false);
    }
  }

  async function startTuPunchout() {
    if (!activeWerkbonId) return;
    setTuPunchoutLoading(true);
    setTuPunchoutError("");
    setTuPunchoutSuccess("");

    try {
      const payload = {
        returnPath: `${location.pathname}${location.search || ""}`,
      };
      if (process.env.REACT_APP_TU_HOOK_URL) {
        payload.hookUrl = process.env.REACT_APP_TU_HOOK_URL;
      }

      const res = await api.post(
        `/werkbonnen/${activeWerkbonId}/integrations/tu/punchout`,
        payload
      );

      const url = res?.data?.tu?.url;
      if (!openOCI(url)) {
        throw new Error("OCI catalogus-url ontbreekt");
      }

      setTuPunchoutSuccess("Catalogus openen...");
    } catch (err) {
      setTuPunchoutError(
        err?.response?.data?.message || "OCI catalogus openen mislukt"
      );
    } finally {
      setTuPunchoutLoading(false);
    }
  }

  async function downloadWorkorderPdf() {
    if (!activeWerkbonId) return;
    setWorkorderPdfLoading(true);
    setWerkbonError("");

    try {
      const res = await api.get(`/werkbonnen/${activeWerkbonId}/pdf`, {
        responseType: "blob",
      });
      const blob =
        res?.data instanceof Blob
          ? res.data
          : new Blob([res?.data], { type: "application/pdf" });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const fileNameBase =
        String(werkbon?.werkbonNummer || werkbon?.nummer || "werkbon").trim() ||
        "werkbon";
      link.href = objectUrl;
      link.download = `${fileNameBase}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
    } catch (err) {
      setWerkbonError(err?.response?.data?.message || "Werkbon PDF downloaden mislukt");
    } finally {
      setWorkorderPdfLoading(false);
    }
  }

  const financialTotals = werkbon?.financialTotals || {};
  const tuIntegrationStatus = werkbon?.integrationStatus?.tu || {};
  const isAutoTimeMode = werkbon?.timeMode === "auto";
  const hourEntriesDirty =
    !isAutoTimeMode &&
    buildHourEntriesFingerprint(hourEntries) !== savedHourEntriesFingerprint;
  const canStartWerkbon = canStartStatus(werkbon?.status);
  const canResumeWerkbon = canResumeStatus(werkbon?.status);
  const canBlockWerkbon = canBlockStatus(werkbon?.status);
  const canAfrondenWerkbon = canAfrondenStatus(werkbon?.status);
  const assignedToCurrentUser = isAssignedToCurrentUser(werkbon, user);
  const canSelfAssignWerkbon = Boolean(werkbon?.selfAssignable) && !assignedToCurrentUser;
  const completionFlow = werkbon?.completionFlow || {};
  const completionState = werkbon?.completionState || {};
  const requireHoursForCompletion = completionFlow?.requireHours !== false;
  const signatureAlreadyCaptured =
    completionFlow?.signatureCaptured === true ||
    werkbon?.completionChecks?.signatureCaptured === true ||
    werkbon?.akkoord?.gegeven === true;
  const customerApprovalUsed = hasCustomerApprovalInput(customerApprovalDraft);
  const effectiveUiPolicy =
    werkbon?.effectiveUiPolicy && typeof werkbon.effectiveUiPolicy === "object"
      ? werkbon.effectiveUiPolicy
      : {};
  const customerApprovalValidationMessage = getCustomerApprovalValidationMessage(
    customerApprovalDraft,
    { required: effectiveUiPolicy.requireSignature === true && !signatureAlreadyCaptured }
  );
  const customerApprovalReady =
    customerApprovalUsed && !customerApprovalValidationMessage;
  const canSubmitAutoTime =
    isAutoTimeMode &&
    (autoTimeBaseEntries.length > 0 || autoAdjustments.length > 0) &&
    werkbon?.timeSubmissionStatus !== "ingediend";

  const executionProps = {
    activeWerkbonId,
    werkbonLoading,
    werkbonError,
    werkbon,
    isAutoTimeMode,
    canStartWerkbon,
    canResumeWerkbon,
    canBlockWerkbon,
    canAfrondenWerkbon,
    canSelfAssignWerkbon,
    completionFlow,
    completionState,
    customerApprovalDraft,
    customerApprovalError,
    customerApprovalUsed,
    customerApprovalReady,
    updateCustomerApprovalField: (field, nextValue) => {
      setCustomerApprovalDraft((prev) =>
        normalizeCustomerApprovalDraft({
          ...prev,
          [field]: field === "approved" ? nextValue === true : nextValue,
        })
      );
      if (customerApprovalError) {
        setCustomerApprovalError("");
      }
      if (werkbonError) {
        setWerkbonError("");
      }
    },
    clearCustomerApproval: () => {
      setCustomerApprovalDraft(EMPTY_CUSTOMER_APPROVAL);
      setCustomerApprovalError("");
      if (werkbonError) {
        setWerkbonError("");
      }
    },
    actieLoading,
    handleUnblockWerkbon,
    handleStartWerkbon,
    handleSelfAssignWerkbon,
    openStopReasonPicker,
    handleAfrondenWerkbon,
    showStopReasonPicker,
    STOP_REASON_OPTIONS,
    handleBlockWerkbon,
    lastStopReason,
    setShowStopReasonPicker,
    latestTimeExtensionRequest,
    setShowTimeExtensionModal,
    timeExtensionSuccess,
    timeExtensionError,
    showTimeExtensionModal,
    timeExtensionLoading,
    timeExtensionRequests,
    workorderDocumentsLoading,
    highlightedSharedDocuments,
    workorderDocuments,
    projectWorkHistory,
    projectWorkHistoryLoading,
    projectWorkHistoryError,
    PHOTO_CATEGORY_OPTIONS,
    photoCategoryDraft,
    setPhotoCategoryDraft,
    handleSelectPhotos,
    photoError,
    photoSuccess,
    selectedPhotos,
    photoEditingId,
    handleEditSelectedPhoto,
    removeSelectedPhoto,
    handleUploadPhotos,
    photoUploading,
    photoUploadProgress,
    werkbonPhotos,
    handleDeleteWerkbonPhoto,
    photoDeletingId,
    hourDraft,
    setHourDraft,
    addHourDraft,
    hourTypeOptions,
    hourEntries,
    hourEntriesDirty,
    requireHoursForCompletion,
    autoTimeBaseEntries,
    autoAdjustments,
    adjustmentDraft,
    setAdjustmentDraft,
    ADJUSTMENT_CATEGORIES,
    addAdjustmentDraft,
    submitAutoTimeForApproval,
    timeSubmitLoading,
    canSubmitAutoTime,
    tuIntegrationStatus,
    startTuPunchout,
    tuPunchoutLoading,
    tuPunchoutError,
    tuPunchoutSuccess,
    downloadWorkorderPdf,
    workorderPdfLoading,
    materialSuggestions,
    materialSuggestionsLoading,
    materialSuggestionsError,
    materialSuggestionSearch,
    setMaterialSuggestionSearch,
    applyMaterialSuggestion,
    materialDraft,
    setMaterialDraft,
    addMaterialDraft,
    removeMaterialEntry,
    materialEntries,
    saveEntries,
    entriesSaving,
    financialTotals,
    handleDaglogSubmit,
    dailyLogForm,
    setDailyLogForm,
    setDailyLogSuccess,
    dailyLogSaving,
    dailyLogError,
    dailyLogSuccess,
  };

  return (
    <div
      className="min-h-[100dvh] bg-slate-100"
      style={{ paddingBottom: "calc(9.5rem + env(safe-area-inset-bottom))" }}
    >
        <div className="mx-auto max-w-7xl px-4 py-4 lg:px-6">
          <WerkbonHeader activeTodayWerkbon={activeTodayWerkbon} />

          <div className="mb-4 space-y-3 lg:hidden">
            <WerkbonSidebar
              mobile
              agendaDate={agendaDate}
              setAgendaDate={setAgendaDate}
              agendaView={agendaView}
              setAgendaView={setAgendaView}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              werkbonSearch={werkbonSearch}
              setWerkbonSearch={setWerkbonSearch}
              navigate={navigate}
              werkbonnenLoading={werkbonnenLoading}
              werkbonnenError={werkbonnenError}
              werkbonnenInView={werkbonnenInAgendaView}
              activeWerkbonId={activeWerkbonId}
              markedWerkbonId={markedWerkbonId}
              openWerkbon={openWerkbon}
            />
          </div>

          {statusFilter === "done" && (
            <section className="mb-4 rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 px-4 py-3">
                <p className="text-sm font-semibold text-slate-900">Afgeronde bonnen</p>
                <p className="text-xs text-slate-500">Werkbonnummer en datum om terug te kijken</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {DONE_RANGE_OPTIONS.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => setDoneListRange(option.key)}
                      className={`rounded-full border px-3 py-1 text-xs font-medium ${
                        doneListRange === option.key
                          ? "border-blue-600 bg-blue-50 text-blue-700"
                          : "border-slate-300 bg-white text-slate-700"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="lg:grid lg:grid-cols-[380px_1fr]">
                <div>
                  {filteredDoneWerkbonnen.length === 0 ? (
                    <p className="px-4 py-3 text-sm text-slate-500">Geen afgeronde bonnen gevonden.</p>
                  ) : (
                    filteredDoneWerkbonnen.map((item) => {
                      const itemId = String(item?._id || "");
                      const isActive = itemId === String(activeWerkbonId || "");
                      return (
                        <button
                          key={itemId}
                          type="button"
                          onClick={() => openWerkbon(itemId, item)}
                          className={`flex w-full items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 text-left text-sm last:border-b-0 ${
                            isActive ? "bg-blue-50 text-blue-700" : "text-slate-800"
                          }`}
                        >
                          <span className="min-w-0">
                            <span className="block truncate font-medium">
                              {item?.werkbonNummer || item?.nummer || "Werkbon"}
                            </span>
                            <span className="mt-0.5 block truncate text-xs text-slate-600">
                              {getWerkbonClientLabel(item)}
                            </span>
                          </span>
                          <span className="shrink-0 text-xs text-slate-500">{formatWerkbonDate(item)}</span>
                        </button>
                      );
                    })
                  )}
                </div>
                <div className="hidden border-l border-slate-200 p-3 lg:block">
                  <main className="rounded-xl border border-slate-200 bg-white p-4">
                    <WerkbonTabs
                      TABS={TABS}
                      activeTab={activeTab}
                      setActiveTab={setActiveTab}
                      agendaDate={agendaDate}
                      statusFilter={statusFilter}
                      setStatusFilter={setStatusFilter}
                      agendaLoading={agendaLoading}
                      agendaError={agendaError}
                      agendaItems={agendaItems}
                      markedWerkbonId={markedWerkbonId}
                      openWerkbon={openWerkbon}
                      activeWerkbonId={activeWerkbonId}
                      showTaakForm={showTaakForm}
                      setShowTaakForm={setShowTaakForm}
                      setTaakError={setTaakError}
                      handleAddTaak={handleAddTaak}
                      actieTypeDraft={actieTypeDraft}
                      setActieTypeDraft={setActieTypeDraft}
                      ACTION_TYPE_OPTIONS={ACTION_TYPE_OPTIONS}
                      taakDraft={taakDraft}
                      setTaakDraft={setTaakDraft}
                      taakSaving={taakSaving}
                      taakError={taakError}
                      werkbonLoading={werkbonLoading}
                      werkbonError={werkbonError}
                      currentTaken={currentTaken}
                      handleVeiligheidMelding={handleVeiligheidMelding}
                      meldingText={meldingText}
                      setMeldingText={setMeldingText}
                      meldingType={meldingType}
                      setMeldingType={setMeldingType}
                      meldingSeverity={meldingSeverity}
                      setMeldingSeverity={setMeldingSeverity}
                      meldingPhoto={meldingPhoto}
                      setMeldingPhoto={setMeldingPhoto}
                      meldingSaving={meldingSaving}
                      meldingError={meldingError}
                      meldingSuccess={meldingSuccess}
                      veiligheidsMeldingen={veiligheidsMeldingen}
                      executionProps={executionProps}
                    />
                  </main>
                </div>
              </div>
            </section>
          )}

          <div className={`grid gap-4 ${statusFilter === "done" ? "lg:hidden" : "lg:grid-cols-[300px_1fr]"}`}>
            <WerkbonSidebar
              agendaDate={agendaDate}
              setAgendaDate={setAgendaDate}
              agendaView={agendaView}
              setAgendaView={setAgendaView}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              werkbonSearch={werkbonSearch}
              setWerkbonSearch={setWerkbonSearch}
              navigate={navigate}
              werkbonnenLoading={werkbonnenLoading}
              werkbonnenError={werkbonnenError}
              werkbonnenInView={werkbonnenInAgendaView}
              activeWerkbonId={activeWerkbonId}
              markedWerkbonId={markedWerkbonId}
              openWerkbon={openWerkbon}
            />

          <main className="rounded-xl border border-slate-200 bg-white p-4 pb-24 lg:h-[calc(100dvh-180px)] lg:overflow-auto lg:pb-4">
            <WerkbonTabs
              TABS={TABS}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              agendaDate={agendaDate}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              agendaLoading={agendaLoading}
              agendaError={agendaError}
              agendaItems={agendaItems}
              markedWerkbonId={markedWerkbonId}
              openWerkbon={openWerkbon}
              activeWerkbonId={activeWerkbonId}
              showTaakForm={showTaakForm}
              setShowTaakForm={setShowTaakForm}
              setTaakError={setTaakError}
              handleAddTaak={handleAddTaak}
              actieTypeDraft={actieTypeDraft}
              setActieTypeDraft={setActieTypeDraft}
              ACTION_TYPE_OPTIONS={ACTION_TYPE_OPTIONS}
              taakDraft={taakDraft}
              setTaakDraft={setTaakDraft}
              taakSaving={taakSaving}
              taakError={taakError}
              werkbonLoading={werkbonLoading}
              werkbonError={werkbonError}
              currentTaken={currentTaken}
              handleVeiligheidMelding={handleVeiligheidMelding}
              meldingText={meldingText}
              setMeldingText={setMeldingText}
              meldingType={meldingType}
              setMeldingType={setMeldingType}
              meldingSeverity={meldingSeverity}
              setMeldingSeverity={setMeldingSeverity}
              meldingPhoto={meldingPhoto}
              setMeldingPhoto={setMeldingPhoto}
              meldingSaving={meldingSaving}
              meldingError={meldingError}
              meldingSuccess={meldingSuccess}
              veiligheidsMeldingen={veiligheidsMeldingen}
              executionProps={executionProps}
            />
          </main>
        </div>

        {activeWerkbonId &&
          (canSelfAssignWerkbon ||
            canStartWerkbon ||
            canResumeWerkbon ||
            canBlockWerkbon ||
            canAfrondenWerkbon) && (
          <div
            className="fixed inset-x-0 z-40 px-3 md:hidden"
            style={{ bottom: "calc(4.9rem + env(safe-area-inset-bottom))" }}
          >
            <div className="mx-auto flex max-w-md flex-wrap gap-2 rounded-xl border bg-white p-2 shadow-lg">
              {canSelfAssignWerkbon && (
                <button
                  type="button"
                  onClick={handleSelfAssignWerkbon}
                  disabled={actieLoading}
                  className="min-h-[44px] min-w-[96px] flex-1 rounded-lg border border-blue-300 bg-white px-3 py-2 text-sm font-medium text-blue-700 disabled:opacity-60"
                >
                  Zelf inplannen
                </button>
              )}
              {(canStartWerkbon || canResumeWerkbon) && (
                <button
                  type="button"
                  onClick={canResumeWerkbon ? handleUnblockWerkbon : handleStartWerkbon}
                  disabled={actieLoading}
                  className="min-h-[44px] min-w-[96px] flex-1 rounded-lg bg-green-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-60"
                >
                  Start werk
                </button>
              )}
              {canBlockWerkbon && (
                <button
                  type="button"
                  onClick={openStopReasonPicker}
                  disabled={actieLoading}
                  className="min-h-[44px] min-w-[96px] flex-1 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-60"
                >
                  Stop werk
                </button>
              )}
              {canAfrondenWerkbon && (
                <button
                  type="button"
                  onClick={handleAfrondenWerkbon}
                  disabled={actieLoading}
                  className="min-h-[44px] min-w-[96px] flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 disabled:opacity-60"
                >
                  Werk gereed
                </button>
              )}
            </div>
          </div>
        )}

        {mobileWerkbonOptions.length > 0 && statusFilter !== "done" && (
          <div
            className="fixed bottom-3 right-3 z-40 md:hidden"
            style={{ bottom: "calc(9.9rem + env(safe-area-inset-bottom))" }}
          >
            <button
              type="button"
              onClick={() => setShowMobileWerkbonSheet(true)}
              className="flex min-h-[46px] items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-lg"
            >
              <span aria-hidden="true">📋</span>
              Bonnen
              <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs">
                {mobileWerkbonOptions.length}
              </span>
            </button>
          </div>
        )}

        {showMobileWerkbonSheet && statusFilter !== "done" && (
          <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true">
            <button
              type="button"
              aria-label="Sluit bonnenlijst"
              onClick={() => setShowMobileWerkbonSheet(false)}
              className="absolute inset-0 bg-black/45"
            />
            <div className="absolute inset-x-0 bottom-0 max-h-[76dvh] rounded-t-2xl bg-white p-4 shadow-xl">
              <div className="mb-3 flex items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Mijn werkbonnen</p>
                  <p className="text-xs text-slate-500">
                    {mobileCompletedCount} gereed van {mobileWerkbonOptions.length}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowMobileWerkbonSheet(false)}
                  className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700"
                >
                  Sluiten
                </button>
              </div>
              <div className="space-y-2 overflow-auto pb-2">
                {mobileWerkbonOptions.map((item) => {
                  const itemId = String(item?._id || "");
                  const isActive = itemId === String(activeWerkbonId || "");
                  const isDone = isWerkbonDoneStatus(item?.status);
                  return (
                    <button
                      key={itemId}
                      type="button"
                      onClick={() => openWerkbon(itemId, item)}
                      className={`w-full rounded-lg border px-3 py-2 text-left ${
                        isActive
                          ? "border-blue-600 bg-blue-50"
                          : "border-slate-200 bg-white"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-900">
                            {item?.werkbonNummer || item?.nummer || "Werkbon"}
                          </p>
                          <p className="mt-0.5 truncate text-xs text-slate-600">
                            {getWerkbonTitle(item)}
                          </p>
                          <p className="mt-1 text-xs text-slate-500">
                            {formatDatum(item?.plannedDate)} •{" "}
                            {formatWerkbonStatusLabel(item?.status)}
                          </p>
                        </div>
                        <span
                          className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-sm font-bold ${
                            isDone
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-slate-100 text-slate-400"
                          }`}
                          title={isDone ? "Werkbon afgerond" : "Nog open"}
                        >
                          ✓
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      <ExtraTimeRequestModal
        open={showTimeExtensionModal}
        onClose={() => setShowTimeExtensionModal(false)}
        onSubmit={handleSubmitTimeExtensionRequest}
        submitting={timeExtensionSubmitting}
        error={timeExtensionError}
        plannedWindowLabel={formatPlannedWindow(werkbon)}
      />
    </div>
  );
}

function resolveStatusFilterFromSearch(search) {
  const params = new URLSearchParams(String(search || ""));
  const requested = String(params.get("status") || "").trim().toLowerCase();
  if (["all", "open", "active", "paused", "done"].includes(requested)) {
    return requested;
  }
  return "open";
}

function isWerkbonDoneStatus(status) {
  const key = String(status || "").trim().toLowerCase();
  return key === "afgerond" || key === "afgemeld" || key === "gesloten";
}

function matchesDoneRange(item, rangeKey) {
  if (rangeKey === "all") return true;
  const dayKey = toISODate(item?.plannedDate || item?.createdAt);
  if (!dayKey) return false;

  if (rangeKey === "today") {
    return dayKey === toISODate(new Date());
  }

  if (rangeKey === "week") {
    return isIsoDateInCurrentWeek(dayKey);
  }

  return true;
}

function isIsoDateInCurrentWeek(dayKey) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dayKey || ""))) return false;
  const [year, month, day] = dayKey.split("-").map((part) => Number(part));
  const candidate = new Date(year, month - 1, day);
  if (Number.isNaN(candidate.getTime())) return false;
  candidate.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const mondayOffset = (today.getDay() + 6) % 7;
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() - mondayOffset);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 7);

  return candidate >= weekStart && candidate < weekEnd;
}

function formatWerkbonDate(item) {
  return formatDatum(item?.plannedDate || item?.createdAt);
}

function getWerkbonClientLabel(item) {
  return (
    item?.clientId?.naam ||
    item?.client?.naam ||
    item?.klant?.naam ||
    item?.projectId?.naam ||
    "-"
  );
}

function formatWerkbonStatusLabel(status) {
  const key = String(status || "").trim().toLowerCase();
  if (key === "gepland") return "Gepland";
  if (key === "in_uitvoering") return "In uitvoering";
  if (key === "tijdelijk_stilgelegd") return "Tijdelijk stilgelegd";
  if (key === "afgerond") return "Afgerond";
  if (key === "afgemeld") return "Afgemeld";
  if (key === "gesloten") return "Gesloten";
  if (key === "gereed_voor_planning") return "Gereed voor planning";
  return status ? String(status) : "-";
}

function parseActionText(value) {
  const raw = String(value || "").trim();
  const match = raw.match(/^\[(.+?)\]\s*(.*)$/);
  if (!match) {
    return { type: "", text: raw };
  }

  const [, type, text] = match;
  return {
    type: String(type || "").trim(),
    text: String(text || "").trim() || raw,
  };
}

function formatActionTypeLabel(value) {
  const key = String(value || "").trim().toLowerCase();
  if (key === "voorbereiding") return "Voorbereiding";
  if (key === "uitvoering") return "Uitvoering";
  if (key === "blokkade") return "Blokkade: ontbrekende info";
  if (key === "nazorg") return "Nazorg";
  if (key === "overig") return "Overig";
  return key ? key : "Actie";
}

function formatActionStatusLabel(value) {
  const key = String(value || "").trim().toLowerCase();
  if (key === "open") return "Open";
  if (key === "bezig") return "Bezig";
  if (key === "wacht-op-info") return "Wacht op info";
  if (key === "gesloten") return "Gesloten";
  return key ? key : "";
}

function canStartStatus(status) {
  const key = String(status || "").trim().toLowerCase();
  return key === "gepland" || key === "uitgegeven" || key === "aangemaakt";
}

function canResumeStatus(status) {
  const key = String(status || "").trim().toLowerCase();
  return key === "tijdelijk_stilgelegd";
}

function canBlockStatus(status) {
  const key = String(status || "").trim().toLowerCase();
  return key === "in_uitvoering";
}

function canAfrondenStatus(status) {
  const key = String(status || "").trim().toLowerCase();
  return key === "in_uitvoering";
}
